import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import FloatingBanner from "../../components/FloatingBanner";
import {
  scheduleInstructorsList,
  scheduleList,
  shuffleScheduleList,
} from "../../data/schedule";

import * as S from "./styled";
import AOS from "aos";
import { Helmet } from "react-helmet-async";

const Schedule = () => {
  const scheduleRef = useRef<HTMLDivElement>(null);
  const shuffleScheduleRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [activeSection, setActiveSection] = useState<
    "schedule" | "shuffleSchedule" | null
  >("schedule");

  const [scrollHeight, setScrollHeight] = useState<number>(180);

  const handleScroll = () => {
    if (
      wrapperRef.current &&
      scheduleRef.current &&
      shuffleScheduleRef.current
    ) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const pricingRect = scheduleRef.current.getBoundingClientRect();

      if (
        pricingRect.top >= wrapperRect.top &&
        pricingRect.bottom > scrollHeight
      ) {
        setActiveSection("schedule");
      } else {
        setActiveSection("shuffleSchedule");
      }
    }
  };

  useEffect(() => {
    const updateScrollHeight = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth >= 1024) {
        setScrollHeight(180);
      } else if (screenWidth >= 768) {
        setScrollHeight(160);
      } else {
        setScrollHeight(120);
      }
    };

    updateScrollHeight();
    window.addEventListener("resize", updateScrollHeight);

    return () => {
      window.removeEventListener("resize", updateScrollHeight);
    };
  }, []);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      const sectionPosition =
        ref.current.getBoundingClientRect().top + window.scrollY; // 섹션 위치 계산
      window.scrollTo({
        top: sectionPosition - scrollHeight + 5,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollHeight]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isReactSnap = navigator.userAgent === "ReactSnap";

    if (!isReactSnap) {
      AOS.init();
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }, []);
  return (
    <>
      <Helmet>
        <title>넵튠 댄스 스튜디오 | 셔플 풋워크 전문 학원</title>

        <meta
          name="description"
          content="셔플 풋워크 전문 트레이닝 센터 | 기초부터 전문인 양성까지"
        />

        <meta
          name="keywords"
          content="넵튠, 넵튠 댄스, 넵튠 댄스 스튜디오, 셔플, 셔플댄스학원, 댄스자격증, 풋워크 수업, 국제실용댄스지도자협회, 국제실용댄스, 풋워크"
        />

        <meta property="og:title" content="넵튠 댄스 스튜디오" />
        <meta
          property="og:description"
          content="셔플 풋워크 전문 트레이닝 센터 | 기초부터 전문인 양성까지"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://neptunedance.com" />
        <meta
          property="og:image"
          content="https://neptunedance.com/og-image.png"
        />
      </Helmet>
      <S.ScheduleWrapper ref={wrapperRef}>
        <div className="inner">
          <S.ScheduleContainer>
            <S.ScheduleNavList>
              <li
                onClick={() => {
                  scrollToSection(scheduleRef);
                }}
                className={activeSection === "schedule" ? "active" : ""}
              >
                수업 시간표
              </li>
              <li
                onClick={() => {
                  scrollToSection(shuffleScheduleRef);
                }}
                className={activeSection === "shuffleSchedule" ? "active" : ""}
              >
                셔플 수업 시간표
              </li>
            </S.ScheduleNavList>
            <div className="contents-area">
              <S.ScheduleWrap ref={scheduleRef}>
                <div className="title-area">
                  <h3
                    data-aos="fade-right"
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    SCHEDULE
                  </h3>
                  <p
                    data-aos="fade-right"
                    data-aos-delay={200}
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    수업 시간표
                  </p>
                </div>
                <ul
                  data-aos="fade"
                  data-aos-delay={300}
                  data-aos-duration={500}
                  data-aos-easing="linear"
                >
                  {scheduleList.map((item) => {
                    return (
                      <S.ScheduleListItem key={item.dayEn}>
                        <div className="day-area">
                          <p
                            className={
                              item.dayEn.trim() === "SAT"
                                ? "blue"
                                : item.dayEn.trim() === "SUN"
                                ? "red"
                                : ""
                            }
                          >
                            {item.dayEn}
                          </p>
                          <p
                            className={
                              item.dayEn.trim() === "SAT"
                                ? "blue"
                                : item.dayEn.trim() === "SUN"
                                ? "red"
                                : ""
                            }
                          >
                            {item.dayKo}
                          </p>
                        </div>

                        <ul className="class-area">
                          {item.class.map((item) => (
                            <li>
                              <dl>
                                <dt>{item.time}</dt>
                                <dd>{item.title}</dd>
                              </dl>
                              <p>{item.instructor}</p>
                            </li>
                          ))}
                        </ul>
                      </S.ScheduleListItem>
                    );
                  })}
                </ul>
              </S.ScheduleWrap>
              <S.ShuffleScheduleWrap ref={shuffleScheduleRef}>
                <div className="title-area">
                  <h3
                    data-aos="fade-right"
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    SHUFFLE SCHEDULE
                  </h3>
                  <p
                    data-aos="fade-right"
                    data-aos-delay={200}
                    data-aos-duration={500}
                    data-aos-easing="linear"
                  >
                    수업 시간표
                  </p>
                </div>
                <ul
                  data-aos="fade"
                  data-aos-delay={200}
                  data-aos-duration={500}
                  data-aos-easing="linear"
                >
                  {shuffleScheduleList.map((item, index) => {
                    return (
                      <S.ScheduleListItem key={item.dayKo} className="shuffle">
                        <div className="day-area">
                          <p>{item.dayEn}</p>
                          <p>{item.dayKo}</p>
                        </div>

                        <ul className="class-area">
                          {item.class.map((item) => (
                            <li>
                              <dl>
                                <dt>{item.time}</dt>
                                <dd>{item.title}</dd>
                              </dl>
                              <p>{item.instructor}</p>
                            </li>
                          ))}
                        </ul>
                      </S.ScheduleListItem>
                    );
                  })}
                </ul>
              </S.ShuffleScheduleWrap>
            </div>
            <div
              className="instructors-area"
              data-aos="fade-left"
              data-aos-duration={500}
              data-aos-easing="linear"
            >
              <h6>담당 강사</h6>
              <ul>
                {scheduleInstructorsList.map((instructor) => (
                  <li>
                    <NavLink to={`/instructors/${instructor.name}`}>
                      <i>
                        <img src={instructor.smallImg} alt={instructor.name} />
                      </i>
                      <p>{instructor.name}</p>
                    </NavLink>
                  </li>
                ))}
              </ul>
              <p>*모든 수업은 예약제로 운영됩니다</p>
            </div>
          </S.ScheduleContainer>
        </div>

        <FloatingBanner
          linkTo="http://pf.kakao.com/_xdxcJZn"
          text="클래스 예약하기"
          target="_blank"
        />
      </S.ScheduleWrapper>
    </>
  );
};

export default Schedule;
